// hooks/useJobs.js
import { useState } from "react";
import { jobsAPI } from "../../services/api";

export const useJobs = ({
  setErrorRecords,
  setErrorJobName,
  setShowErrorsModal,
  setReuploadJob,
  setShowReuploadModal,
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [masterData, setMasterData] = useState([]);
  const [comparisonResults, setComparisonResults] = useState([]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.getAll();
      const jobsData = response.data.data || [];

      // Load error counts for each job
      const jobsWithErrorCounts = await Promise.all(
        jobsData.map(async (job) => {
          try {
            const resultsResponse = await jobsAPI.getComparisonResults(job.id);
            if (resultsResponse.data.success && resultsResponse.data.data) {
              const errorCount = resultsResponse.data.data.filter(
                (record) =>
                  record.Remark &&
                  record.Remark !== "ตรงกันทุกประการ" &&
                  record.Remark.trim() !== ""
              ).length;
              return {
                ...job,
                error_count: errorCount,
                total_records: resultsResponse.data.data.length,
              };
            }
            return { ...job, error_count: 0, total_records: 0 };
          } catch (error) {
            console.warn(
              `Could not load comparison results for job ${job.id}:`,
              error
            );
            return { ...job, error_count: 0, total_records: 0 };
          }
        })
      );

      setJobs(jobsWithErrorCounts);
    } catch (error) {
      console.error("Failed to load jobs:", error);
      alert("ไม่สามารถโหลดข้อมูลงานได้");
    } finally {
      setLoading(false);
    }
  };

  const viewJobDetails = async (job) => {
    setMasterData([]);
    setComparisonResults([]);

    try {
      console.log(
        `📊 Loading job details for: ${job.job_name} (ID: ${job.id})`
      );

      // Load master data for this job from database
      const masterResponse = await jobsAPI.getMasterData(job.id);
      if (
        masterResponse.data.success &&
        masterResponse.data.data &&
        masterResponse.data.data.length > 0
      ) {
        console.log(
          `✅ Loaded ${masterResponse.data.data.length} master records for job ${job.id}`
        );
        setMasterData(masterResponse.data.data);
      } else {
        console.log(`⚠️ No stored master data found for job ${job.id}`);
        setMasterData([]);
      }

      // Load comparison results from database
      try {
        const resultsResponse = await jobsAPI.getComparisonResults(job.id);
        if (resultsResponse.data.success && resultsResponse.data.data) {
          console.log(
            `✅ Loaded ${resultsResponse.data.data.length} comparison results for job ${job.id}`
          );
          setComparisonResults(resultsResponse.data.data);
        } else {
          console.log(`⚠️ No comparison results found for job ${job.id}`);
          setComparisonResults([]);
        }
      } catch (resultsError) {
        console.warn(
          "Could not load comparison results:",
          resultsError.message
        );
        setComparisonResults([]);
      }
    } catch (error) {
      console.error("Failed to load job details:", error);
      alert("ไม่สามารถโหลดรายละเอียดงานได้: " + error.message);
    }
  };

  const showErrorRecords = async (job) => {
    try {
      setLoading(true);
      const resultsResponse = await jobsAPI.getComparisonResults(job.id);

      if (resultsResponse.data.success && resultsResponse.data.data) {
        const errors = resultsResponse.data.data.filter(
          (record) =>
            record.Remark &&
            record.Remark !== "ตรงกันทุกประการ" &&
            record.Remark.trim() !== ""
        );

        setErrorRecords(errors);
        setErrorJobName(job.job_name);
        setShowErrorsModal(true);
      } else {
        alert("ไม่พบข้อมูลการเปรียบเทียบสำหรับงานนี้");
      }
    } catch (error) {
      console.error("Failed to load error records:", error);
      alert("ไม่สามารถโหลดข้อมูลข้อผิดพลาดได้");
    } finally {
      setLoading(false);
    }
  };

  const startAR04Reupload = (job) => {
    setReuploadJob(job);
    setShowReuploadModal(true);
  };

  const deleteJob = async (job) => {
    const confirmMessage =
      `คุณต้องการลบงาน "${job.job_name}" หรือไม่?\n\n` +
      `⚠️ การดำเนินการนี้จะลบข้อมูลต่อไปนี้ออกจากฐานข้อมูล:\n` +
      `• งาน "${job.job_name}"\n` +
      `• ไฟล์ AR04 ที่เกี่ยวข้อง\n` +
      `• ผลการเปรียบเทียบทั้งหมด\n` +
      `• ข้อมูล MASTER FILE และ COMPARE FILE\n\n` +
      `❌ การดำเนินการนี้ไม่สามารถยกเลิกได้`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      console.log(`🗑️ Deleting job: ${job.job_name} (ID: ${job.id})`);
      await jobsAPI.delete(job.id);

      // Remove job from local state
      setJobs((prevJobs) => prevJobs.filter((j) => j.id !== job.id));

      alert(
        `ลบงาน "${job.job_name}" สำเร็จ!\n\n✅ ข้อมูลทั้งหมดที่เกี่ยวข้องถูกลบออกจากฐานข้อมูลแล้ว`
      );
    } catch (error) {
      console.error("Delete job failed:", error);

      if (error.response?.status === 404) {
        alert("ไม่พบงานที่ต้องการลบ");
        loadJobs(); // Refresh jobs list
      } else if (error.response?.status === 403) {
        alert("คุณไม่มีสิทธิ์ในการลบงานนี้");
      } else if (error.response?.status === 500) {
        alert("เกิดข้อผิดพลาดกับเซิร์ฟเวอร์ กรุณาลองใหม่ภายหลัง");
      } else {
        alert(
          "เกิดข้อผิดพลาดในการลบงาน: " +
            (error.response?.data?.message || error.message)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const exportJobResults = async (jobId, type) => {
    try {
      const response = await jobsAPI.exportResults(jobId, type);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Find job name for filename
      const job = jobs.find((j) => j.id === jobId);
      const jobName = job ? job.job_name : "job";

      link.setAttribute("download", `${jobName}_${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
      alert("ไม่สามารถส่งออกข้อมูลได้");
    }
  };

  return {
    jobs,
    loading,
    masterData,
    comparisonResults,
    loadJobs,
    viewJobDetails,
    deleteJob,
    exportJobResults,
    showErrorRecords,
    startAR04Reupload,
  };
};
